import { useState, useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import Papa from 'papaparse'
import { useCollection, useFirestoreMutations } from '../../hooks/useFirestore'
import './RelationshipImport.css'

const VALID_TYPES = ['pc', 'npc']
const VALID_GENDERS = ['male', 'female', 'nonbinary', 'other']

export default function CharacterImport({ isOpen, onClose }) {
  const { data: characters } = useCollection('characters', { orderByField: 'name' })
  const { add } = useFirestoreMutations('characters')

  const [parsedData, setParsedData] = useState([])
  const [errors, setErrors] = useState([])
  const [importing, setImporting] = useState(false)
  const [importResult, setImportResult] = useState(null)

  // Build lookup for existing characters
  const existingNames = new Set(characters.map(c => c.name.toLowerCase()))

  const downloadTemplate = () => {
    const headers = ['name', 'type', 'active', 'hidden', 'deceased', 'class', 'race', 'gender', 'age', 'alignment', 'height', 'weight', 'hair', 'eyes', 'skin', 'faith', 'faction', 'lore', 'dndBeyondUrl', 'sortOrder']
    const exampleRows = [
      ['Drakhlith', 'pc', 'true', 'false', 'false', 'Warlock', 'Drow', 'male', '120', 'Chaotic Neutral', '5\'10"', '165 lbs', 'White', 'Red', 'Obsidian', 'Lolth', 'House Baenre', 'A mysterious drow with a dark past.', 'https://www.dndbeyond.com/characters/12345', '1'],
      ['Sofia', 'pc', 'true', 'false', 'false', 'Paladin', 'Human', 'female', '28', 'Lawful Good', '5\'6"', '140 lbs', 'Auburn', 'Green', 'Fair', 'Torm', 'Order of the Gauntlet', '', 'https://www.dndbeyond.com/characters/67890', '2'],
      ['The Innkeeper', 'npc', 'false', 'false', 'false', '', 'Halfling', 'nonbinary', 'Middle-aged', '', '', '', '', '', '', '', '', 'Runs the Gilded Tankard.', '', ''],
      ['Fallen Hero', 'pc', 'false', 'false', 'true', 'Fighter', 'Human', 'male', '45', 'Lawful Good', '', '', '', '', '', '', '', 'Died bravely in battle.', '', '3'],
    ]

    const csvContent = [
      headers.join(','),
      ...exampleRows.map(row => row.map(cell =>
        cell.includes(',') || cell.includes('\n') ? `"${cell.replace(/"/g, '""')}"` : cell
      ).join(','))
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'characters_template.csv'
    link.click()
    URL.revokeObjectURL(url)
  }

  const validateRow = (row, rowIndex) => {
    const issues = []

    // Required: name
    const name = row.name?.trim()
    if (!name) {
      issues.push('Missing name')
    } else if (existingNames.has(name.toLowerCase())) {
      issues.push(`Character "${name}" already exists`)
    }

    // Validate type (default to npc if empty)
    const type = row.type?.trim().toLowerCase() || 'npc'
    if (row.type && !VALID_TYPES.includes(type)) {
      issues.push(`Invalid type: "${row.type}" (valid: ${VALID_TYPES.join(', ')})`)
    }

    // Validate gender if provided
    const gender = row.gender?.trim().toLowerCase() || ''
    if (gender && !VALID_GENDERS.includes(gender)) {
      issues.push(`Invalid gender: "${row.gender}" (valid: ${VALID_GENDERS.join(', ')})`)
    }

    // Parse sortOrder (header normalized to lowercase)
    const sortOrderStr = row.sortorder?.trim()
    const sortOrder = sortOrderStr ? parseInt(sortOrderStr, 10) : 0

    // Parse active flag (default to true)
    const activeStr = row.active?.trim().toLowerCase()
    const active = activeStr ? ['true', '1', 'yes', 'active'].includes(activeStr) : true

    // Parse hidden flag (default to false)
    const hiddenStr = row.hidden?.trim().toLowerCase()
    const hidden = hiddenStr ? ['true', '1', 'yes', 'hidden'].includes(hiddenStr) : false

    // Parse deceased flag (default to false)
    const deceasedStr = row.deceased?.trim().toLowerCase()
    const deceased = deceasedStr ? ['true', '1', 'yes', 'deceased', 'dead'].includes(deceasedStr) : false

    return {
      row: rowIndex + 1,
      data: row,
      character: {
        name: name || '',
        type: VALID_TYPES.includes(type) ? type : 'npc',
        active,
        hidden,
        deceased,
        class: row.class?.trim() || '',
        race: row.race?.trim() || '',
        gender: VALID_GENDERS.includes(gender) ? gender : '',
        age: row.age?.trim() || '',
        alignment: row.alignment?.trim() || '',
        height: row.height?.trim() || '',
        weight: row.weight?.trim() || '',
        hair: row.hair?.trim() || '',
        eyes: row.eyes?.trim() || '',
        skin: row.skin?.trim() || '',
        faith: row.faith?.trim() || '',
        faction: row.faction?.trim() || '',
        lore: row.lore?.trim() || '',
        profileImageUrl: '',
        dndBeyondUrl: row.dndbeyondurl?.trim() || '',
        relationships: [],
        eventIds: [],
        sortOrder: isNaN(sortOrder) ? 0 : sortOrder
      },
      valid: issues.length === 0,
      issues
    }
  }

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0]
    if (!file) return

    setImportResult(null)

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (header) => header.trim().toLowerCase().replace(/\s+/g, ''),
      complete: (results) => {
        const validated = results.data.map((row, i) => validateRow(row, i))
        setParsedData(validated)

        const errs = validated.filter(v => !v.valid)
        setErrors(errs)
      },
      error: (err) => {
        setErrors([{ row: 0, issues: [`Parse error: ${err.message}`] }])
      }
    })
  }, [existingNames])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'text/plain': ['.csv', '.txt']
    },
    multiple: false
  })

  const handleImport = async () => {
    const validRows = parsedData.filter(r => r.valid)
    if (validRows.length === 0) return

    setImporting(true)

    try {
      let created = 0

      for (const row of validRows) {
        await add(row.character)
        created++
      }

      setImportResult({
        success: true,
        message: `Successfully imported ${created} characters.`
      })

      // Clear parsed data after success
      setParsedData([])
      setErrors([])
    } catch (err) {
      setImportResult({
        success: false,
        message: `Import failed: ${err.message}`
      })
    }

    setImporting(false)
  }

  const handleClose = () => {
    setParsedData([])
    setErrors([])
    setImportResult(null)
    onClose()
  }

  if (!isOpen) return null

  const validCount = parsedData.filter(r => r.valid).length
  const invalidCount = parsedData.filter(r => !r.valid).length

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content import-modal" onClick={e => e.stopPropagation()}>
        <h2>Import Characters from CSV</h2>

        <div className="import-instructions">
          <p>CSV columns: <code>name, type, active, hidden, deceased, class, race, gender, age, alignment, height, weight, hair, eyes, skin, faith, faction, lore, dndBeyondUrl, sortOrder</code></p>
          <p className="help-text">
            Required: name <br />
            Types: {VALID_TYPES.join(', ')} (default: npc) <br />
            Active: true/false (default: true) <br />
            Hidden: true/false (default: false) - hidden characters are only visible in admin <br />
            Deceased: true/false (default: false) - marks character as dead <br />
            Genders: {VALID_GENDERS.join(', ')} <br />
            All other fields are optional
          </p>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={downloadTemplate}
          >
            Download Template
          </button>
        </div>

        {/* Dropzone */}
        <div
          {...getRootProps()}
          className={`dropzone ${isDragActive ? 'active' : ''}`}
        >
          <input {...getInputProps()} />
          {isDragActive ? (
            <p>Drop CSV file here...</p>
          ) : (
            <p>Drag & drop a CSV file, or click to select</p>
          )}
        </div>

        {/* Results */}
        {importResult && (
          <div className={`import-result ${importResult.success ? 'success' : 'error'}`}>
            {importResult.message}
          </div>
        )}

        {/* Preview */}
        {parsedData.length > 0 && (
          <div className="import-preview">
            <h3>
              Preview: {validCount} valid, {invalidCount} with errors
            </h3>

            <div className="preview-table-wrapper">
              <table className="preview-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Class</th>
                    <th>Race</th>
                    <th>Gender</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {parsedData.slice(0, 50).map((row, i) => (
                    <tr key={i} className={row.valid ? '' : 'invalid'}>
                      <td>{row.row}</td>
                      <td>{row.data.name}</td>
                      <td>{row.data.type || 'npc'}</td>
                      <td>{row.data.class || '-'}</td>
                      <td>{row.data.race || '-'}</td>
                      <td>{row.data.gender || '-'}</td>
                      <td>
                        {row.valid ? (
                          <span className="status-ok">OK</span>
                        ) : (
                          <span className="status-error" title={row.issues.join('\n')}>
                            Error
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {parsedData.length > 50 && (
              <p className="help-text">Showing first 50 of {parsedData.length} rows</p>
            )}

            {/* Error details */}
            {errors.length > 0 && (
              <div className="import-errors">
                <h4>Errors ({errors.length})</h4>
                <ul>
                  {errors.slice(0, 10).map((e, i) => (
                    <li key={i}>
                      Row {e.row}: {e.issues.join('; ')}
                    </li>
                  ))}
                  {errors.length > 10 && (
                    <li>...and {errors.length - 10} more errors</li>
                  )}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="modal-actions">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={handleClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleImport}
            disabled={importing || validCount === 0}
          >
            {importing ? 'Importing...' : `Import ${validCount} Characters`}
          </button>
        </div>
      </div>
    </div>
  )
}
